// Kapcsolattartó-szerkesztő tiszta segédei (nem-komponens): form-értékek típusa, alapértékek, a @atlas/contracts
// write-sémája (a resolverhez) és a lokalizált zod-hibatérkép. Külön fájl a komponenstől, hogy a react-refresh
// (Fast Refresh) tiszta maradjon. A minta az employees/form.ts — a validálás forrás-igazsága a contract (NEM saját
// séma). Az identity-mag (e-mail/nevek) + a partner-profil (telefon/beosztás/becenév). Fiók-kötés itt NINCS
// (user-döntés): a kapcsolattartót a fiók oldalán lehet fiókhoz rendelni, ott dől el a portál-hozzáférés, a
// szerep és az elsődleges jelölés. A `type` SOHA nem kerül a body-ba (a szerver dönti el, hogy partner).
import { ContactCreateBodySchema, ContactUpdateBodySchema } from '@atlas/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { type TFunction } from 'i18next';
import { type Resolver } from 'react-hook-form';
import { type z } from 'zod';

/**
 * A form mező-értékei (string-alapú identity-/profil-mezők).
 * A `type` NINCS a form-ban (a szerver rendeli partner-nek). A `nickname` a create-nél megadható; edit-nél a
 * ContactRow visszahordozza (ha az Odin projektálja) → előtöltjük, a mentés DIRTY-alapon küldi: érintetlen mező
 * → nem megy (nem nulláz némán meglévő becenevet), piszkált üres mező → törlés (lásd `toContactBody`).
 */
export interface ContactFormValues {
  givenName: string;
  familyName: string;
  /** Belépési e-mail — KÖTELEZŐ (a partner-identity egyedi kulcsa). */
  email: string;
  phone: string;
  jobTitle: string;
  nickname: string;
}

/**
 * A `displayName` SZÁRMAZTATOTT, nem beírható. Kézzel írva befagyasztja a névsorrendet, és
 * versenyez a strukturált részekkel: a felület a nevet MINDIG a `givenName`/`familyName` párból rendezi a
 * NÉZŐ nyelvén (`formatName` / szerver-oldalon `personName`), tehát egy begépelt „Kovács János" magyarul is,
 * angolul is ugyanúgy jelenne meg — épp a locale-helyes sorrend veszne el. A mezőt azért írjuk mégis, mert a
 * platform `name` claimje (OIDC) és a többi consumer app ezt olvassa fallbackként; a rendszerben viszont SOHA
 * nem ez a forrás-igazság, csak akkor látszik, ha a név-részek hiányoznak.
 */
function toDisplayName(given: string | null, family: string | null): string | null {
  return [given, family].filter(Boolean).join(' ') || null;
}

/** Üres alapértékek (a reset a szerkesztendő sorból tölti fel; create → üres). */
export const EMPTY_CONTACT_FORM: ContactFormValues = {
  givenName: '',
  familyName: '',
  email: '',
  phone: '',
  jobTitle: '',
  nickname: '',
};

/** A contract write-body típusai. */
type ContactCreateBody = z.infer<typeof ContactCreateBodySchema>;
type ContactUpdateBody = z.infer<typeof ContactUpdateBodySchema>;

/** Üres string → null (a nullable mezőkre); a trim a beillesztés-maradékot is levágja. */
function nn(s: string): string | null {
  return s.trim() === '' ? null : s.trim();
}

/**
 * Form-értékek → CREATE wire-body (partner-provisioning). Az e-mail trimmelve; a nullable profil-mezők üres → null.
 * A `type` SOHA nem kerül bele.
 */
export function toContactCreateBody(v: ContactFormValues): ContactCreateBody {
  return {
    email: v.email.trim(),
    // a név KÖTELEZŐ: a vágott szöveg megy — az üres a szerződés `min(1)`-én bukik (mező-hiba), nem `null`
    givenName: v.givenName.trim(),
    familyName: v.familyName.trim(),
    displayName: toDisplayName(nn(v.givenName), nn(v.familyName)),
    phone: nn(v.phone),
    jobTitle: nn(v.jobTitle),
    nickname: nn(v.nickname),
  };
}

/**
 * Form-értékek → EDIT wire-body (parciális PATCH). Az e-mailt mindig küldjük (a form kötelezővé teszi); a nullable
 * profil-mezők üres → null (mező-törlés az Odin oldalán). A `type` NEM szerkeszthető (nincs a body-ban).
 *
 * A `nickname` DIRTY-alapú (`includeNickname`): a becenév az edit-formban előtöltésre kerül a sorból (az Odin
 * read-projekció adja). Ha nem piszkálták (`includeNickname` hamis), a mezőt KIHAGYJUK (`undefined` → nem PATCH) —
 * védelem arra az esetre, ha a sor valamiért mégsem hozná: egy másik mező mentése SOHA nem nulláz némán meglévő
 * becenevet. Ha piszkálták: üres → `null` (törlés), érték → beállítás. Default `true` (a resolver-validáláshoz).
 */
export function toContactBody(
  v: ContactFormValues,
  opts?: { includeNickname?: boolean },
): ContactUpdateBody {
  const includeNickname = opts?.includeNickname ?? true;
  return {
    email: v.email.trim(),
    // a név KÖTELEZŐ: a vágott szöveg megy — az üres a szerződés `min(1)`-én bukik (mező-hiba), nem `null`
    givenName: v.givenName.trim(),
    familyName: v.familyName.trim(),
    displayName: toDisplayName(nn(v.givenName), nn(v.familyName)),
    phone: nn(v.phone),
    jobTitle: nn(v.jobTitle),
    nickname: includeNickname ? nn(v.nickname) : undefined,
  };
}

/**
 * Lokalizált zod-hibatérkép: a zod-issue (mező-path + kód) → a `validation:contacts.*` réteg üzenete. Az e-mail a
 * fő validált mező; ismeretlen issue → generikus üzenet.
 */
export function buildContactErrorMap(t: TFunction): z.ZodErrorMap {
  return (issue, ctx) => {
    const field = issue.path[0];
    if (field === 'email') {
      return { message: t('validation:contacts.email') };
    }
    if (field === 'familyName' || field === 'givenName') {
      return { message: t(`validation:contacts.${field}`) };
    }
    return { message: ctx.defaultError };
  };
}

/**
 * RHF-resolver a contract UPDATE-sémából: a validálás a WIRE-alakon fut (a `toContactBody` normalizálása után), a
 * hiba-pathok 1:1 a form-mezők nevei. Sikerkor az EREDETI (string-alapú) form-értékek mennek tovább (a wire-body-t a
 * mutáció építi). A minta a buildEmployeeResolver.
 */
export function buildContactResolver(t: TFunction): Resolver<ContactFormValues> {
  const base = zodResolver(ContactUpdateBodySchema, {
    errorMap: buildContactErrorMap(t),
  }) as unknown as Resolver<ContactFormValues>;
  return async (values, context, options) => {
    const result = await base(
      toContactBody(values) as unknown as ContactFormValues,
      context,
      options,
    );
    if (Object.keys(result.errors).length > 0) {
      return { values: {}, errors: result.errors };
    }
    return { values, errors: {} };
  };
}

/** CREATE-resolver: a validálás a `ContactCreateBodySchema` WIRE-alakján fut (e-mail kötelező); a
 *  hiba-pathok 1:1 a form-mezők. Sikerkor az EREDETI form-értékek mennek tovább (a body-t a mutáció építi). */
export function buildContactCreateResolver(t: TFunction): Resolver<ContactFormValues> {
  const base = zodResolver(ContactCreateBodySchema, {
    errorMap: buildContactErrorMap(t),
  }) as unknown as Resolver<ContactFormValues>;
  return async (values, context, options) => {
    const result = await base(
      toContactCreateBody(values) as unknown as ContactFormValues,
      context,
      options,
    );
    if (Object.keys(result.errors).length > 0) {
      return { values: {}, errors: result.errors };
    }
    return { values, errors: {} };
  };
}
